/**
 * Transporte comum aos clientes Peitho (onboarding, waitlist pública e admin).
 * Cada cliente continua dono da sua classe de erro — os chamadores fazem
 * `instanceof` — e a passa para cá.
 */
import { resolveApiBase } from './adminRequest'

type PeithoErrorClass = new (
  message: string,
  status: number,
  code: string | null,
  body: unknown,
) => Error

export const peithoBaseUrl = (): Promise<string> => resolveApiBase('peitho')

/** Bearer da sessão Cerberus; sem token lança `ErrorClass('no access token', 401)`. */
export async function peithoBearer(ErrorClass: PeithoErrorClass): Promise<string> {
  const { getAccessToken } = await import('@/lib/cerberus/tokens')
  const token = getAccessToken()
  if (!token) throw new ErrorClass('no access token', 401, null, null)
  return token
}

async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return null
  }
}

function errorCodeOf(body: unknown): string | null {
  if (body && typeof body === 'object') {
    const err = (body as { error?: { code?: string } }).error
    if (err && typeof err.code === 'string') return err.code
  }
  return null
}

/**
 * Corpo JSON da resposta (`null` se não for JSON). Não-2xx lança
 * `ErrorClass('<label> -> <status>', status, body.error.code, body)`.
 */
export async function ensurePeithoOk(
  res: Response,
  label: string,
  ErrorClass: PeithoErrorClass,
): Promise<unknown> {
  const body = await readJson(res)
  if (!res.ok) {
    throw new ErrorClass(`${label} -> ${res.status}`, res.status, errorCodeOf(body), body)
  }
  return body
}

/** `{ data: T }` vira `T`; qualquer outro corpo passa como está. */
export function unwrapData<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: T }).data
  }
  return body as T
}
